import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, Tab, Input, Button } from "barte-design-system";
import { PlusIcon, TrashIcon, CheckIcon } from "@phosphor-icons/react";

import { Skeleton } from "@shared/components";

import type { MccTabsProps } from "./mcc-tabs.type";
import styles from "./mcc-tabs.module.css";

import { DEFAULT_MCC_CONFIG } from "../../modal-company-plan.constants";

export const MccTabs = ({
    data,
    activeTab,
    isLoading,
    onChangeData,
    onTabChange
}: MccTabsProps) => {
    const { t } = useTranslation();
    const [isAddingMcc, setIsAddingMcc] = useState(false);
    const [newMcc, setNewMcc] = useState("");

    const mccsKeys = Object.keys(data.tax?.taxConfigsByMcc || {}).sort((a, b) => {
        if (a === "default") return -1;
        if (b === "default") return 1;
        return Number(a) - Number(b);
    });

    const handleAddMcc = () => {
        if (!newMcc.trim()) return;

        const mccToAdd = newMcc.trim();
        const currentMccs = { ...(data.tax?.taxConfigsByMcc || {}) };

        if (!currentMccs[mccToAdd]) {
            currentMccs[mccToAdd] = DEFAULT_MCC_CONFIG;
            onChangeData({
                ...data,
                tax: {
                    ...data.tax,
                    taxConfigsByMcc: currentMccs
                }
            });
        }

        setNewMcc("");
        setIsAddingMcc(false);
        onTabChange(mccToAdd);
    };

    const handleDeleteMcc = (mccKey: string) => {
        const currentMccs = { ...(data.tax?.taxConfigsByMcc || {}) };
        delete currentMccs[mccKey];

        onChangeData({
            ...data,
            tax: {
                ...data.tax,
                taxConfigsByMcc: currentMccs
            }
        });

        if (activeTab === mccKey) {
            const nextKeys = Object.keys(currentMccs);
            onTabChange(nextKeys.length > 0 ? nextKeys[0] : "");
        }
    };

    if (isLoading) {
        return (
            <div className={styles['tabs-container']}>
                <Skeleton width="4rem" height="1.5rem" />
                <div className={styles['skeleton-tabs']}>
                    <Skeleton width="4.25rem" height="2rem" />
                    <Skeleton width="4.25rem" height="2rem" />
                    <Skeleton width="4.25rem" height="2rem" />
                </div>
            </div>
        );
    }

    return (
        <div className={styles['tabs-container']}>
            <span className={styles['mcc-label']}>{t("backoffice.pageModalCompanyPlanMccLabel")}</span>
            <Tabs>
                {mccsKeys.map((key) => (
                    <Tab
                        key={key}
                        active={activeTab === key}
                        onClick={() => {
                            onTabChange(key);
                            setIsAddingMcc(false);
                        }}
                    >
                        <div className={styles['tab-content']}>
                            {key}

                            <TrashIcon
                                size={14}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteMcc(key);
                                }}
                            />

                        </div>
                    </Tab>
                ))}

                {isAddingMcc ? (
                    <div className={styles['add-mcc-wrapper']}>
                        <Input
                            type="text"
                            className={styles['mcc-input']}
                            value={newMcc}
                            onChange={(e) => setNewMcc(e.target.value)}
                            placeholder={t("backoffice.pageModalCompanyPlanAddMccPlaceholder")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleAddMcc();
                                }
                            }}
                            autoFocus
                        />
                        <Button
                            className={styles['add-mcc-confirm-btn']}
                            onClick={handleAddMcc}
                            disabled={!newMcc.trim()}
                        >
                            <CheckIcon size={16} />
                        </Button>
                    </div>
                ) : (
                    <Tab key="add-mcc" active={activeTab === "add-mcc"} onClick={() => setIsAddingMcc(true)}>
                        <PlusIcon size={16} />
                    </Tab>
                )}
            </Tabs>
        </div>
    );
};
